/**
 * Search and tag filters.
 */

import type { FC } from 'react';
import { Filter, Search, UploadCloud } from 'lucide-react';
import { Input, LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  NO_TAGS_VALUE,
  TagFilterDropdown,
} from '@/features/smswiser-campaigns/components/shared/pickers/TagFilterDropdown';
import { MultiTagFilterDropdown } from '@/features/smswiser-campaigns/components/shared/pickers/multi-tag';

import { CountryFilterMenu } from './CountryFilterMenu';
import { ScheduledSortToolbar } from './ScheduledSortToolbar';

import type { FiltersRowProps } from './FiltersRow.props';

export type { FiltersRowProps };

export const FiltersRow: FC<FiltersRowProps> = ({
  searchQuery,
  onSearchChange,
  tagFilter = '',
  tagFilterOptions = [],
  tagColors,
  tagsLoading,
  onTagFilterChange,
  staticTagFilters = [],
  staticTagFilterOptions = [],
  staticTagColors,
  onStaticTagFiltersChange,
  showTagFilters = false,
  showCountryFilter = false,
  countryFilterOptions = [],
  selectedCountryKeys = [],
  onCountryKeysChange,
  showScheduledSortToolbar = false,
  scheduledSortBy = null,
  scheduledSortDir = 'desc',
  onToggleScheduledSendTimeSort,
  showSmswiserDraftsFilter = false,
  smswiserDraftsOnly = false,
  onToggleSmswiserDraftsOnly,
}) => (
  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:flex-nowrap">
    {showSmswiserDraftsFilter && onToggleSmswiserDraftsOnly && (
      <button
        type="button"
        onClick={onToggleSmswiserDraftsOnly}
        aria-pressed={smswiserDraftsOnly}
        title="Show only drafts staged on SMSWiser"
        className={cn(
          'flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors',
          smswiserDraftsOnly
            ? 'border-orange-500 bg-orange-500/15 text-orange-600 dark:text-orange-400'
            : 'border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )}
      >
        <UploadCloud className="h-3.5 w-3.5" aria-hidden />
        SMSWiser drafts
      </button>
    )}
    {showCountryFilter && onCountryKeysChange && countryFilterOptions.length > 0 && (
      <CountryFilterMenu
        options={countryFilterOptions}
        selectedKeys={selectedCountryKeys}
        onChange={onCountryKeysChange}
      />
    )}
    {showScheduledSortToolbar && onToggleScheduledSendTimeSort && (
      <ScheduledSortToolbar
        sortBy={scheduledSortBy}
        sortDir={scheduledSortDir}
        onToggleSendTimeOrder={onToggleScheduledSendTimeSort}
      />
    )}
    {showTagFilters && (onTagFilterChange || onStaticTagFiltersChange) && (
      <div className="flex shrink-0 items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        {tagsLoading ? (
          <span className="flex h-8 items-center gap-1.5 text-xs text-muted-foreground">
            <LoadingSpinner size="xs" />
            Tags…
          </span>
        ) : (
          <>
            {onTagFilterChange && (
              <TagFilterDropdown
                value={tagFilter}
                options={tagFilterOptions}
                tagColors={tagColors}
                displayLabel={
                  tagFilter === NO_TAGS_VALUE ? 'No tags' : tagFilter ? tagFilter : 'All tags'
                }
                onSelect={onTagFilterChange}
                placeholder="All tags"
                compact
              />
            )}
            {onStaticTagFiltersChange && (
              <MultiTagFilterDropdown
                values={staticTagFilters}
                options={staticTagFilterOptions}
                tagColors={staticTagColors}
                onChange={onStaticTagFiltersChange}
                placeholder="Static tag"
                noneLabel="No tag"
                compact
              />
            )}
          </>
        )}
      </div>
    )}
    <div className="relative min-w-0 flex-1 sm:max-w-[280px]">
      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        type="search"
        placeholder="Search drop ID, gateway, leads, title…"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="h-8 pl-9 pr-3 text-sm"
        aria-label="Search drops"
      />
    </div>
  </div>
);
